import { Injectable, inject } from '@angular/core'
import { Subscription } from 'rxjs'

import { ContentChangeNotification, MeetingEntityType } from './meeting-entity-types'
import { MeetingHubService } from './meeting-hub.service'

export interface MeetingContentSyncOptions {
    meetingId: () => string
    entityTypes: MeetingEntityType[]
    onRefresh: (notification: ContentChangeNotification) => void
    /** When true, remote refreshes are deferred until editing ends. */
    isEditing?: () => boolean
    /** Called when a refresh is deferred because the user is mid-edit. */
    onDeferredRefresh?: (notification: ContentChangeNotification) => void
}

/**
 * Subscribes to live-meeting content-change broadcasts and coalesces rapid bursts
 * into a single refresh callback (last-write-wins convergence).
 */
@Injectable({
    providedIn: 'root',
})
export class MeetingContentSyncService {
    private hub = inject(MeetingHubService)

    subscribe(options: MeetingContentSyncOptions): Subscription {
        let debounceTimer: ReturnType<typeof setTimeout> | null = null
        let pendingNotification: ContentChangeNotification | null = null

        const scheduleRefresh = (notification: ContentChangeNotification) => {
            pendingNotification = notification
            if (debounceTimer) clearTimeout(debounceTimer)
            debounceTimer = setTimeout(() => {
                if (pendingNotification) {
                    options.onRefresh(pendingNotification)
                    pendingNotification = null
                }
                debounceTimer = null
            }, 300)
        }

        const runRefresh = (notification: ContentChangeNotification) => {
            if (options.isEditing?.()) {
                options.onDeferredRefresh?.(notification)
                return
            }
            scheduleRefresh(notification)
        }

        const combined = new Subscription()

        combined.add(
            this.hub.contentChanged$.subscribe(notification => {
                const currentMeetingId = options.meetingId()
                if (!currentMeetingId || notification.meetingId.toLowerCase() !== currentMeetingId.toLowerCase()) {
                    return
                }
                if (!options.entityTypes.includes(notification.entityType)) {
                    return
                }
                runRefresh(notification)
            }),
        )

        combined.add(
            this.hub.contentResync$.subscribe(meetingId => {
                const currentMeetingId = options.meetingId()
                if (!currentMeetingId || meetingId.toLowerCase() !== currentMeetingId.toLowerCase()) {
                    return
                }
                runRefresh({
                    meetingId,
                    entityType: options.entityTypes[0],
                    operation: 'update',
                    entityId: '',
                })
            }),
        )

        return combined
    }

    /** @deprecated Use {@link subscribe} — resync is included automatically. */
    subscribeResync(options: MeetingContentSyncOptions): Subscription {
        return this.subscribe(options)
    }

    /** Run a pending refresh after a local cell edit completes. */
    flushPending(options: MeetingContentSyncOptions, pending: ContentChangeNotification | null): void {
        if (pending && !options.isEditing?.()) {
            options.onRefresh(pending)
        }
    }
}
